// AI assisted development
import { Component, Input, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ConsolidatedSubsService } from 'src/app/@core/services/consolidatedsubs-service';
import { ToastrService } from 'ngx-toastr';
import {
  ConsolidatedSubsDecision,
  IConsolidatedSubsApprovalRequest,
  IConsolidatedSubsItem,
  IGetConsolidatedSubsResponse,
  IItemDecision,
  ISubstitutionDecisionGroup
} from 'src/app/@core/models/consolidated-subs';

export interface IConsolidatedSubsTableRow {
  key: string;
  isSubstitution: boolean;
  originalItem?: IConsolidatedSubsItem;
  item: IConsolidatedSubsItem;
  decision: ConsolidatedSubsDecision | null;
}

@Component({
  selector: 'sk-consolidated-subs-modal',
  templateUrl: './consolidated-subs-modal.component.html',
  styleUrls: ['./consolidated-subs-modal.component.scss']
})
export class ConsolidatedSubsModalComponent implements OnInit {

  @Input() consolidatedSubs: IGetConsolidatedSubsResponse;
  @Input() storeSourceKey: string;
  @Input() orderSourceKey: string;

  substitutionRows: IConsolidatedSubsTableRow[] = [];
  nonOriginalRows: IConsolidatedSubsTableRow[] = [];
  isSubmitting = false;

  constructor(
    private modalService: NgbModal,
    private consolidatedSubsService: ConsolidatedSubsService,
    private toastrService: ToastrService
  ) {}

  ngOnInit() {
    this.buildTableRows();
  }

  buildTableRows() {
    this.substitutionRows = [];
    this.nonOriginalRows = [];

    (this.consolidatedSubs.substitutions || []).forEach((substitution, substitutionIndex) => {
      (substitution.substitutedItems || []).forEach((item, itemIndex) => {
        this.substitutionRows.push({
          key: `sub-${substitutionIndex}-${itemIndex}-${item.orderItemSourceKey}`,
          isSubstitution: true,
          originalItem: substitution.originalItem,
          item,
          decision: null
        });
      });
    });

    (this.consolidatedSubs.nonOriginalOrderItems || []).forEach((item, itemIndex) => {
      this.nonOriginalRows.push({
        key: `non-${itemIndex}-${item.orderItemSourceKey}`,
        isSubstitution: false,
        item,
        decision: null
      });
    });
  }

  setDecision(row: IConsolidatedSubsTableRow, decision: ConsolidatedSubsDecision) {
    row.decision = decision;
  }

  isDecisionSelected(row: IConsolidatedSubsTableRow, decision: ConsolidatedSubsDecision): boolean {
    return row.decision === decision;
  }

  get allRows(): IConsolidatedSubsTableRow[] {
    return [...this.substitutionRows, ...this.nonOriginalRows];
  }

  get allDecisionsMade(): boolean {
    return this.allRows.length > 0 && this.allRows.every(row => row.decision !== null);
  }

  get canSubmit(): boolean {
    return this.allDecisionsMade && !this.isSubmitting;
  }

  formatQuantity(item: IConsolidatedSubsItem): string {
    if (item.weight != null) {
      return `${item.quantity} / ${item.weight}`;
    }
    return `${item.quantity}`;
  }

  dismiss() {
    if (this.isSubmitting) {
      return;
    }
    this.closeModal();
  }

  private closeModal() {
    this.modalService.dismissAll();
  }

  buildApprovalRequest(): IConsolidatedSubsApprovalRequest {
    const substitutionGroups = new Map<string, ISubstitutionDecisionGroup>();

    this.substitutionRows.forEach(row => {
      const originalLineNumber = row.originalItem.lineNumber;
      if (!substitutionGroups.has(originalLineNumber)) {
        substitutionGroups.set(originalLineNumber, {
          originalLineNumber,
          substitutedItemDecisions: []
        });
      }
      substitutionGroups.get(originalLineNumber).substitutedItemDecisions.push({
        lineNumber: row.item.lineNumber,
        approved: row.decision === 'accept'
      });
    });

    const addedItemDecisions: IItemDecision[] = this.nonOriginalRows.map(row => ({
      lineNumber: row.item.lineNumber,
      approved: row.decision === 'accept'
    }));

    return {
      substitutionDecisions: Array.from(substitutionGroups.values()),
      addedItemDecisions
    };
  }

  async submit() {
    if (!this.canSubmit) {
      this.toastrService.warning('Please accept or reject every item before submitting');
      return;
    }

    const request = this.buildApprovalRequest();

    this.isSubmitting = true;
    try {
      const response = await this.consolidatedSubsService.submitConsolidatedSubs(
        this.storeSourceKey,
        this.orderSourceKey,
        request
      );
      if (response && response.responseCode >= 400) {
        this.toastrService.error(response.responseMessage || 'Could not submit consolidated substitutions');
        return;
      }
      this.toastrService.success('Consolidated substitutions submitted successfully');
      this.closeModal();
    } catch {
      this.toastrService.error('Could not submit consolidated substitutions');
    } finally {
      this.isSubmitting = false;
    }
  }
}
